/**
 * Shared (client + server) plausibility checks for the jump-link telemetry.
 *
 * `clickY` is a viewport coordinate, `scrollY` the effective scroll offset a
 * jump applied. Together they yield the absolute document position that was
 * shown: documentY = scrollY + clickY. This module validates that combination
 * so implausible values (negative, NaN, off-viewport, beyond the scrollable
 * area) are detected instead of silently poisoning the analytics.
 */

/** Upper sanity bound for any pixel coordinate we accept. */
export const MAX_PIXEL = 200_000;
/** Tolerance in px for rounding and sub-pixel layout differences. */
export const PIXEL_TOLERANCE = 2;

export type SectionPositionInput = {
  clickY?: number | undefined;
  scrollY?: number | undefined;
  /** Optional: only known on the client. */
  viewportHeight?: number | undefined;
  /** Optional: only known on the client. */
  documentHeight?: number | undefined;
};

export type SectionPositionResult =
  | { valid: true; documentY: number | null; reason: null }
  | { valid: false; documentY: null; reason: string };

function isPlausiblePixel(value: number) {
  return Number.isFinite(value) && value >= -PIXEL_TOLERANCE && value <= MAX_PIXEL;
}

/**
 * Derives the absolute document position that was shown, or explains why the
 * numbers are not plausible. Missing values are allowed (some events carry
 * only one of the two) — `documentY` is then null but the result stays valid.
 */
export function deriveSectionPosition({
  clickY,
  scrollY,
  viewportHeight,
  documentHeight,
}: SectionPositionInput): SectionPositionResult {
  const invalid = (reason: string): SectionPositionResult => ({
    valid: false,
    documentY: null,
    reason,
  });

  if (clickY !== undefined) {
    if (!isPlausiblePixel(clickY)) return invalid(`clickY out of range: ${clickY}`);
    if (
      typeof viewportHeight === "number" &&
      viewportHeight > 0 &&
      clickY > viewportHeight + PIXEL_TOLERANCE
    ) {
      return invalid(`clickY ${clickY} exceeds viewport height ${viewportHeight}`);
    }
  }

  if (scrollY !== undefined) {
    if (!isPlausiblePixel(scrollY)) return invalid(`scrollY out of range: ${scrollY}`);
    if (
      typeof documentHeight === "number" &&
      typeof viewportHeight === "number" &&
      documentHeight > 0 &&
      viewportHeight > 0
    ) {
      const maxScroll = Math.max(0, documentHeight - viewportHeight);
      if (scrollY > maxScroll + PIXEL_TOLERANCE) {
        return invalid(`scrollY ${scrollY} exceeds maximum scroll ${maxScroll}`);
      }
    }
  }

  if (clickY === undefined && scrollY === undefined) {
    return { valid: true, documentY: null, reason: null };
  }

  const documentY = Math.round(Math.max(0, (scrollY ?? 0) + (clickY ?? 0)));
  if (documentY > MAX_PIXEL) return invalid(`derived documentY out of range: ${documentY}`);

  return { valid: true, documentY, reason: null };
}

/**
 * Validates and logs. Returns the result so callers can decide whether to send
 * the coordinates at all.
 */
export function validateSectionPosition(
  context: string,
  input: SectionPositionInput,
): SectionPositionResult {
  const result = deriveSectionPosition(input);
  if (!result.valid) {
    console.error(`[analytics] implausible section position (${context}): ${result.reason}`);
  }
  return result;
}
